import { stringifyUrl } from 'query-string'

import { apiRequest, fetchWithAuthorization } from '../lib/api'
import { botFlowSelector } from '../store/flow/selectors/botFlowSelector'
import { getBotsApiUrl } from '../apiService'
import { confirm } from '../lib/bootbox'
// eslint-disable-next-line no-useless-rename
import { appActions as appActions } from '../store/app'
import * as actionTypes from '../constants/actionTypes'
import { deployBotActions } from '../store/deployBot'
import { launchHostingActions } from '../../src/store/launchHosting'
import { getDeployErrorSelector } from '../store/deployBot/selectors'
import { toastNotifyDeploySuccess, toastNotifySettingBotVersionFailure } from './toasts'
import { loadedBotVersionSelector } from '../store/flow/selectors/loadedBotVersionSelector'
import { deployedBotVersionSelector } from '../features/hostingVersions/selectors/deployedVersionSelector'
import { ServerApiTag } from '../store/serverApi'
import { botsApi } from '../features/serverBots/api/botsApi'
import { validateSsmlInFlowThunk } from '../features/flowValidation'
import { isThereSsmlValidationErrorSelector } from '../store/ssml/selectors'
import { latestVersionFlowSelector } from '../features/serverBots/selectors'

const API_URL = getBotsApiUrl()

export function addDialogs(dialogs) {
  return { type: actionTypes.ADD_DIALOGS, payload: dialogs }
}

export function disableStepNlpInStepTypeDefaults() {
  return { type: actionTypes.DISABLE_STEP_NLP_IN_STEP_TYPE_DEFAULTS }
}

export function launchHosting(botId, query) {
  return async dispatch => {
    try {
      const url = stringifyUrl({ query, url: `${API_URL}/${botId}/hosting` })
      await fetchWithAuthorization(url, {
        method: 'POST',
        checkOk: true,
        parseJson: false,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ originUrl: window.location.origin })
      })
    } catch (error) {
      dispatch(
        launchHostingActions.launchHostingFailure('There was a problem with creating new hosting.')
      )
    }
  }
}

export function updateHosting(botId, file) {
  return async dispatch => {
    dispatch(appActions.updateHosting())
    try {
      await fetchWithAuthorization(`${API_URL}/${botId}/update`, {
        method: 'POST',
        checkOk: true,
        parseJson: false,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ originUrl: window.location.origin, file })
      })

      return true
    } catch (error) {
      dispatch(appActions.updateHostingFailure('There was a problem with updating bot.'))

      toastNotifySettingBotVersionFailure(error?.message)
      return false
    }
  }
}

export function renameDialog(id, name) {
  return { type: actionTypes.RENAME_DIALOG, payload: { id, name } }
}

export function deployBot() {
  return async (dispatch, getState) => {
    const state = getState()
    dispatch(deployBotActions.setIsDeploying(true))

    await dispatch(validateSsmlInFlowThunk(true))
    if (isThereSsmlValidationErrorSelector('full')(getState())) {
      return
    }

    const { id: botId, version } = botFlowSelector(state)

    const url = `${API_URL}/${botId}/deploy`

    dispatch(deployBotActions.deployBot())

    try {
      await apiRequest(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        checkOk: true,
        parseJson: false,
        requireToken: true,
        useResponseMessage: true
      })

      dispatch(botsApi.util.invalidateTags([ServerApiTag.BOT_VERSIONS_LIST(botId)]))
      dispatch(deployBotActions.deployBotSuccess())
      toastNotifyDeploySuccess(version)
    } catch (error) {
      dispatch(deployBotActions.deployBotFailure(error?.message || true))

      if (error.code && error.code === 'EntityAlreadyExists') {
        return await deploymentError(
          url,
          'Warning! This version has been already deployed in the past.',
          dispatch
        )
      }

      await deploymentError(url, error.message || '', dispatch)
    }
  }
}

async function deploymentError(url, message, dispatch) {
  const messageAlert = []

  messageAlert.push(
    `An error occurred during deployment. Try it again or contact <a href="https://feedyou.atlassian.net/servicedesk/customer/portals" target="_blank" rel="noopener noreferrer">our support</a>.<br /><br />API URL: ${url}`
  )

  if (message) {
    messageAlert.push(`Message: ${message}`)
  }

  await confirmRetry(messageAlert, dispatch)
}

async function confirmRetry(message, dispatch) {
  const isConfirmed = await confirm({
    title: '❌ Deploy failed',
    message: message.join('<br />'),
    buttons: {
      confirm: {
        label: 'Retry',
        className: 'btn-positive'
      }
    }
  })

  if (!isConfirmed) {
    return
  }

  dispatch(deployBot())
}

export function confirmDeployBot() {
  return async (dispatch, getState) => {
    const state = getState()
    const deployError = getDeployErrorSelector(state)
    const loadedBotVersion = loadedBotVersionSelector(state)
    const deployedBotVersion = deployedBotVersionSelector(state)

    if (deployError) {
      const message = `Do you really want to try force-deploy of last saved version v${loadedBotVersion} to the bot instance even though it did not respond?`
      if (await confirm(message)) {
        return dispatch(deployBot())
      }
      return
    }

    if (!deployedBotVersion) {
      return dispatch(deployBot())
    }

    const message = `Do you really want to deploy last saved version v${loadedBotVersion} to the bot instead of currently deployed version v${deployedBotVersion}?`
    if (await confirm(message)) {
      return dispatch(deployBot())
    }
  }
}

export function moveStep(srcDialogId, destDialogId, srcIndex, destIndex) {
  return {
    type: actionTypes.MOVE_STEP,
    payload: { srcDialogId, destDialogId, srcIndex, destIndex }
  }
}

export function moveAnswer({
  srcDialogId,
  destDialogId,
  srcStepId,
  destStepId,
  srcIndex,
  destIndex
}) {
  return {
    type: actionTypes.MOVE_ANSWER,
    payload: { srcDialogId, destDialogId, srcStepId, destStepId, srcIndex, destIndex }
  }
}

export function updateStepProperties({
  stepId,
  formData,
  parentDialogId,
  subset = '',
  substepId = '',
  substepIndex = undefined
}) {
  return {
    type: actionTypes.UPDATE_STEP_PROPERTIES,
    payload: { stepId, subset, formData, substepId, substepIndex, parentDialogId }
  }
}

export function updateBotProperties(properties) {
  return { type: actionTypes.UPDATE_BOT_PROPERTIES, payload: properties }
}

export function deleteDialogOccurences(dialogId) {
  return { type: actionTypes.DELETE_DIALOG_OCCURENCES, dialogId }
}

export function deleteStep(dialogId, stepIndex) {
  return {
    type: actionTypes.DELETE_STEP,
    dialogId,
    stepIndex
  }
}

export function addStep(stepId, dialogId, index, step) {
  step.id = stepId
  return {
    type: actionTypes.ADD_STEP,
    dialogId,
    index,
    step: { ...step }
  }
}

export function deleteDialogs(dialogs) {
  return { type: actionTypes.DELETE_DIALOGS, payload: [...dialogs] }
}

export function discardChanges(originalVersion) {
  return { type: actionTypes.DISCARD_CHANGES, payload: originalVersion }
}

window.discardChanges = () => {
  const store = window.Store
  const originalVersion = latestVersionFlowSelector(store.getState())

  store.dispatch(discardChanges(originalVersion))
}
